<template>
  <div class="barcode-scanner-wrapper">
    <video
      ref="video"
      autoplay
      muted
      playsinline
      class="barcode-scanner-video"
    ></video>
    <div v-if="errorMessage" class="barcode-scanner-error">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { BrowserMultiFormatReader } from '@zxing/browser'
import { BarcodeFormat, DecodeHintType } from '@zxing/library'

const SCANNER_FORMATS = [
  BarcodeFormat.QR_CODE,
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
  BarcodeFormat.CODE_93,
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.ITF,
  BarcodeFormat.CODABAR,
  BarcodeFormat.DATA_MATRIX,
  BarcodeFormat.PDF_417,
]

export default {
  name: 'BarcodeScanner',
  props: {
    active: {
      type: Boolean,
      default: true,
    },
  },
  data(){
    return {
      codeReader: null,
      controls: null,
      errorMessage: '',
      lastDecodedBarcode: '',
      lastDecodedAt: 0,
      startRequestId: 0,
    }
  },
  watch: {
    active: {
      handler(active){
        if(active) this.start()
        else this.stop()
      },
      immediate: true,
    },
  },
  beforeDestroy(){
    this.stop()
  },
  methods: {
    async start(){
      if(this.controls || !this.active) return
      const requestId = this.startRequestId + 1
      this.startRequestId = requestId

      await this.$nextTick()
      if(!this.active || requestId !== this.startRequestId || !this.$refs.video) return

      this.errorMessage = ''

      const hints = new Map()
      hints.set(DecodeHintType.POSSIBLE_FORMATS, SCANNER_FORMATS)
      hints.set(DecodeHintType.TRY_HARDER, true)

      this.codeReader = new BrowserMultiFormatReader(hints, {
        delayBetweenScanAttempts: 100,
        delayBetweenScanSuccess: 800,
      })

      const constraints = {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      }

      try {
        const controls = await this.codeReader.decodeFromConstraints(
          constraints,
          this.$refs.video,
          this.onDecodeResult
        )
        if(!this.active || requestId !== this.startRequestId) {
          controls.stop()
          return
        }
        this.controls = controls
        this.$emit('loaded')
      } catch (error) {
        if(requestId !== this.startRequestId) return
        this.errorMessage = 'Could not start camera scanner'
        this.$emit('error', error)
      }
    },
    stop(){
      this.startRequestId += 1
      if(this.controls && typeof this.controls.stop === 'function') {
        this.controls.stop()
      }
      this.controls = null
      this.codeReader = null
    },
    onDecodeResult(result, error){
      if(result) {
        this.emitDecodedBarcode(result.getText())
        return
      }

      if(error && error.name !== 'NotFoundException') {
        this.$emit('error', error)
      }
    },
    emitDecodedBarcode(barcode){
      const now = Date.now()
      if(barcode === this.lastDecodedBarcode && now - this.lastDecodedAt < 1000) return

      this.lastDecodedBarcode = barcode
      this.lastDecodedAt = now
      this.$emit('decode', barcode)
    },
  },
}
</script>

<style lang="scss" scoped>
.barcode-scanner-wrapper {
  position: relative;
  width: 100%;
}

.barcode-scanner-video {
  display: block;
  width: 100%;
  border-radius: 8px;
}

.barcode-scanner-error {
  margin-top: 8px;
  color: var(--v-error-base);
  font-size: 0.75rem;
  text-align: center;
}
</style>
